import { useState, FormEvent } from 'react';

export default function DiagnosticForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    segment: '',
    employees: '',
    revenue: '',
    mainChallenge: '',
    goals: '',
    urgency: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ifjomusuhg53kqao5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          segment: '',
          employees: '',
          revenue: '',
          mainChallenge: '',
          goals: '',
          urgency: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="diagnostic-section" className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Diagnóstico Gratuito
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Preencha o formulário e receba uma análise inicial do seu negócio 
            com recomendações personalizadas em até 24 horas
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
          <form id="diagnostic-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                  placeholder="Seu nome"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Telefone/WhatsApp *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nome da Empresa *
                </label>
                <input
                  type="text"
                  name="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                  placeholder="Nome da sua empresa"
                />
              </div>
            </div>

            {/* Business Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Segmento de Atuação *
                </label>
                <select
                  name="segment"
                  required
                  value={formData.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Selecione...</option>
                  <option value="comercio">Comércio</option>
                  <option value="servicos">Serviços</option>
                  <option value="industria">Indústria</option>
                  <option value="tecnologia">Tecnologia</option>
                  <option value="alimentacao">Alimentação</option>
                  <option value="saude">Saúde e Bem-estar</option>
                  <option value="educacao">Educação</option>
                  <option value="construcao">Construção</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Número de Funcionários *
                </label>
                <select
                  name="employees"
                  required
                  value={formData.employees}
                  onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Selecione...</option>
                  <option value="apenas-eu">Apenas eu (MEI)</option>
                  <option value="2-5">2 a 5 funcionários</option>
                  <option value="6-10">6 a 10 funcionários</option>
                  <option value="11-20">11 a 20 funcionários</option>
                  <option value="mais-20">Mais de 20 funcionários</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Faturamento Mensal Aproximado *
              </label>
              <select
                name="revenue"
                required
                value={formData.revenue}
                onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Selecione...</option>
                <option value="ate-10k">Até R$ 10.000</option>
                <option value="10k-30k">R$ 10.000 a R$ 30.000</option>
                <option value="30k-50k">R$ 30.000 a R$ 50.000</option>
                <option value="50k-100k">R$ 50.000 a R$ 100.000</option>
                <option value="mais-100k">Acima de R$ 100.000</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Principal Desafio Atual *
              </label>
              <textarea
                name="mainChallenge"
                required
                value={formData.mainChallenge}
                onChange={(e) => setFormData({ ...formData, mainChallenge: e.target.value })}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm resize-none"
                placeholder="Descreva o principal problema ou desafio que você enfrenta no seu negócio..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.mainChallenge.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                O que você espera alcançar com o programa? *
              </label>
              <textarea
                name="goals"
                required
                value={formData.goals}
                onChange={(e) => setFormData({ ...formData, goals: e.target.value })}
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm resize-none"
                placeholder="Descreva seus objetivos e expectativas..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.goals.length}/500 caracteres
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Quando você gostaria de começar? *
              </label>
              <select
                name="urgency"
                required
                value={formData.urgency}
                onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Selecione...</option>
                <option value="imediato">Imediatamente</option>
                <option value="1-mes">Dentro de 1 mês</option>
                <option value="2-3-meses">Em 2-3 meses</option>
                <option value="apenas-informacao">Apenas buscando informações</option>
              </select>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-coral-500 text-white rounded-lg font-bold text-lg hover:bg-coral-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Enviando...' : 'Receber Diagnóstico Gratuito'}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center">
                <i className="ri-check-circle-line text-2xl mb-2"></i>
                <p className="font-semibold">Diagnóstico enviado com sucesso!</p>
                <p className="text-sm mt-1">Entraremos em contato em até 24 horas.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center">
                <i className="ri-error-warning-line text-2xl mb-2"></i>
                <p className="font-semibold">Erro ao enviar formulário</p>
                <p className="text-sm mt-1">Por favor, tente novamente.</p>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              Seus dados estão seguros e não serão compartilhados com terceiros
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
